import { EnemyData, EnemyIntent, PlayerState } from '../types';
import { FEATURES } from '../features';
import {
  ensureEnemyStance,
  advanceEnemyStance,
  withCastIntentIfNeeded,
  stanceIntentValue,
  resolveCastPayload,
  forceBossStanceForPhase,
} from './StanceSystem';
import { getEnemyIntentReduction, applyStatus } from '../combat/StatusSystem';

function detectBossPhase(enemy: EnemyData): number {
  const hpPercent = enemy.hp / Math.max(1, enemy.maxHp);
  if (hpPercent > 0.66) return 1;
  if (hpPercent > 0.32) return 2;
  return 3;
}

function applyBossPhaseScript(enemy: EnemyData): void {
  if (!FEATURES.bossPhaseScripts || !enemy.boss) return;
  const nextPhase = detectBossPhase(enemy);
  const previousPhase = enemy.phase ?? 1;

  if (nextPhase !== previousPhase) {
    enemy.phase = nextPhase;
    enemy.patternIndex = 0;
    enemy.phaseTransitionLock = 1;
  } else {
    enemy.phase = nextPhase;
  }

  if (enemy.phasePatterns && enemy.phasePatterns[nextPhase - 1]) {
    enemy.pattern = enemy.phasePatterns[nextPhase - 1];
  }

  forceBossStanceForPhase(enemy);
}

export function getEnemyIntent(enemy: EnemyData): EnemyIntent {
  if (FEATURES.bossPhaseScripts) {
    applyBossPhaseScript(enemy);
  }

  ensureEnemyStance(enemy);

  const idx = enemy.patternIndex % enemy.pattern.length;
  let intent = { ...enemy.pattern[idx] };

  if (FEATURES.enemyStances) {
    intent = withCastIntentIfNeeded(enemy, intent);
  }

  if (enemy.phaseTransitionLock && enemy.phaseTransitionLock > 0 && intent.type === 'summon') {
    // Fairness gate: first post-phase turn cannot be summon burst.
    intent = { type: 'defend', value: 8, description: 'Recalibrates after phase shift' };
  }

  if (intent.type === 'attack') {
    const weakenedReduction = getEnemyIntentReduction(enemy);
    const stanceScaled = FEATURES.enemyStances ? stanceIntentValue(enemy, intent.value) : intent.value;
    intent.value = Math.max(0, stanceScaled - weakenedReduction);
  }

  return intent;
}

export function advanceEnemyPattern(enemy: EnemyData): void {
  enemy.patternIndex++;
  if (enemy.phaseTransitionLock && enemy.phaseTransitionLock > 0) {
    enemy.phaseTransitionLock -= 1;
  }
  if (FEATURES.enemyStances) {
    advanceEnemyStance(enemy);
  }
}

export function executeEnemyIntent(
  enemy: EnemyData,
  intent: EnemyIntent,
  playerBlock: number,
  player?: PlayerState,
): { damageToPlayer: number; enemyBlockGain: number; summonTemplateId?: string; combatLog?: string[] } {
  const combatLog: string[] = [];

  switch (intent.type) {
    case 'attack': {
      let attackValue = intent.value;
      // Mirror Golem special: attack equals remaining block
      if (enemy.name === 'Mirror Golem' && intent.description?.includes('remaining block')) {
        attackValue = enemy.block;
      }
      const damageToPlayer = Math.max(0, attackValue - playerBlock);
      return { damageToPlayer, enemyBlockGain: 0, combatLog };
    }

    case 'defend':
      return { damageToPlayer: 0, enemyBlockGain: intent.value, combatLog };

    case 'buff': {
      if (intent.description?.includes('Heals')) {
        const healAmount = intent.value;
        enemy.hp = Math.min(enemy.maxHp, enemy.hp + healAmount);
        combatLog.push(`${enemy.name} heals ${healAmount}`);
      }
      return { damageToPlayer: 0, enemyBlockGain: 0, combatLog };
    }

    case 'summon': {
      const summonTemplateId = intent.summonTemplateId ?? enemy.summonTemplateId;
      if (summonTemplateId) {
        combatLog.push(`${enemy.name} summons ${summonTemplateId}`);
      }
      return {
        damageToPlayer: 0,
        enemyBlockGain: 0,
        summonTemplateId,
        combatLog,
      };
    }

    case 'special': {
      if (FEATURES.enemyStances && enemy.stance === 'cast' && player) {
        resolveCastPayload(enemy, player, { combatLog });
      }

      if (enemy.name === 'Crystal Guard' && intent.description?.includes('last damage')) {
        const blockGain = Math.max(0, enemy.maxHp - enemy.hp);
        return { damageToPlayer: 0, enemyBlockGain: Math.min(blockGain, 15), combatLog };
      }

      if (enemy.name === 'Wraith') {
        if (player) applyStatus(player, 'jammed', 1, 1);
        combatLog.push('Wraith hexes your ability slot (JAMMED)');
      }

      if (enemy.name === 'Void Eater' && intent.description?.includes('cards in hand')) {
        const handSize = player?.hand.length ?? 0;
        const damageToPlayer = Math.max(0, handSize - playerBlock);
        return { damageToPlayer, enemyBlockGain: 0, combatLog };
      }

      return { damageToPlayer: 0, enemyBlockGain: 0, combatLog };
    }

    default:
      return { damageToPlayer: 0, enemyBlockGain: 0, combatLog };
  }
}
